/**
 * Generates crisp procedural vector/canvas textures for Zelvis Antigravity
 * Ensures the game works autonomously with zero missing assets or HTTP asset 404s.
 */
export function generateGameTextures(scene) {
  const textures = scene.textures;

  // 1. ZELVIS DETAILED ANIMATION FRAMES (80x96 Canvas)
  const zelvisFrames = [
    'zelvis_idle',
    'zelvis_run_1',
    'zelvis_run_2',
    'zelvis_run_3',
    'zelvis_run_4',
    'zelvis_run_5',
    'zelvis_run_6',
    'zelvis_slide',
    'zelvis_skid',
    'zelvis_flip'
  ];

  zelvisFrames.forEach((key) => {
    if (!textures.exists(key)) {
      const canvas = textures.createCanvas(key, 80, 96);
      const ctx = canvas.getContext();
      drawDetailedZelvis(ctx, key);
      canvas.refresh();
    }
  });

  // Friction dust / smoke particle for slide and skid
  if (!textures.exists('particle_smoke')) {
    const canvas = textures.createCanvas('particle_smoke', 20, 20);
    const ctx = canvas.getContext();
    const grad = ctx.createRadialGradient(10, 10, 2, 10, 10, 9);
    grad.addColorStop(0, 'rgba(255, 235, 150, 0.9)');
    grad.addColorStop(0.5, 'rgba(200, 220, 240, 0.6)');
    grad.addColorStop(1, 'rgba(200, 220, 240, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(10, 10, 9, 0, Math.PI * 2);
    ctx.fill();
    canvas.refresh();
  }



  // Slide Button for Touch UI
  if (!textures.exists('btn_slide')) {
    const canvas = textures.createCanvas('btn_slide', 80, 80);
    const ctx = canvas.getContext();

    // Outer neon glow
    const grad = ctx.createRadialGradient(40, 40, 28, 40, 40, 39);
    grad.addColorStop(0, 'rgba(245, 158, 11, 0.4)');
    grad.addColorStop(1, 'rgba(245, 158, 11, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(40, 40, 39, 0, Math.PI * 2);
    ctx.fill();

    // Dark glass disc
    ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.fill();

    // Golden amber glowing border
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.stroke();

    // Downward & forward slide chevron
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(25, 29);
    ctx.lineTo(40, 44);
    ctx.lineTo(55, 29);
    ctx.lineTo(49, 23);
    ctx.lineTo(40, 32);
    ctx.lineTo(31, 23);
    ctx.closePath();
    ctx.fill();

    // Text: SLIDE
    ctx.fillStyle = '#fde047';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SLIDE', 40, 60);

    canvas.refresh();
  }



  // Aegis Shield Forcefield Aura Texture
  if (!textures.exists('shield_aura')) {
    const canvas = textures.createCanvas('shield_aura', 110, 110);
    const ctx = canvas.getContext();
    const grad = ctx.createRadialGradient(55, 55, 25, 55, 55, 52);
    grad.addColorStop(0, 'rgba(56, 189, 248, 0.15)');
    grad.addColorStop(0.7, 'rgba(56, 189, 248, 0.45)');
    grad.addColorStop(0.9, 'rgba(250, 204, 21, 0.8)');
    grad.addColorStop(1, 'rgba(250, 204, 21, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(55, 55, 52, 0, Math.PI * 2);
    ctx.fill();

    // Electric arcs around perimeter
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(55, 55, 48, 0, Math.PI * 2);
    ctx.stroke();

    canvas.refresh();
  }



  // Overdrive electric lightning particle
  if (!textures.exists('particle_lightning')) {
    const canvas = textures.createCanvas('particle_lightning', 24, 24);
    const ctx = canvas.getContext();
    ctx.fillStyle = '#fef08a';
    ctx.shadowColor = '#38bdf8';
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.moveTo(14, 2);
    ctx.lineTo(6, 12);
    ctx.lineTo(12, 12);
    ctx.lineTo(8, 22);
    ctx.lineTo(18, 10);
    ctx.lineTo(12, 10);
    ctx.closePath();
    ctx.fill();
    canvas.refresh();
  }



  // 2. Marble Floor & Ceiling Platform
  if (!textures.exists('platform_marble')) {
    const canvas = textures.createCanvas('platform_marble', 120, 36);
    const ctx = canvas.getContext();

    // Carrara marble gradient base
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.2, '#f8fafc');
    grad.addColorStop(0.7, '#e2e8f0');
    grad.addColorStop(1, '#94a3b8');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Marble veins
    ctx.strokeStyle = 'rgba(203, 213, 225, 0.6)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 12); ctx.lineTo(35, 18); ctx.lineTo(65, 8); ctx.lineTo(120, 20);
    ctx.moveTo(20, 28); ctx.lineTo(70, 22); ctx.lineTo(105, 30);
    ctx.stroke();

    // Golden Neon Top Running Rail (continuous across tiles)
    const goldRail = ctx.createLinearGradient(0, 0, 0, 5);
    goldRail.addColorStop(0, '#fef08a');
    goldRail.addColorStop(0.5, '#f59e0b');
    goldRail.addColorStop(1, '#b45309');
    ctx.fillStyle = goldRail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous Greek Meander Gold Inlay
    drawContinuousMeander(ctx, 120, 11, 14, '#b45309', 1.8);
    drawContinuousMeander(ctx, 120, 11, 14, '#f59e0b', 1.0);

    // Bottom bevel shadow
    ctx.fillStyle = '#64748b';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }



  // 3. Floating Cloud Platform
  if (!textures.exists('platform_cloud')) {
    const canvas = textures.createCanvas('platform_cloud', 140, 40);
    const ctx = canvas.getContext();

    // Lower cyan energy lightning glow
    ctx.fillStyle = 'rgba(56, 189, 248, 0.3)';
    ctx.beginPath();
    ctx.ellipse(70, 28, 62, 11, 0, 0, Math.PI * 2);
    ctx.fill();

    // Cloud billows (shaded white puffs)
    const cloudGrad = ctx.createLinearGradient(0, 6, 0, 36);
    cloudGrad.addColorStop(0, '#ffffff');
    cloudGrad.addColorStop(0.65, '#f1f5f9');
    cloudGrad.addColorStop(1, '#cbd5e1');
    ctx.fillStyle = cloudGrad;

    ctx.beginPath();
    ctx.arc(32, 24, 18, 0, Math.PI * 2);
    ctx.arc(62, 17, 22, 0, Math.PI * 2);
    ctx.arc(92, 19, 20, 0, Math.PI * 2);
    ctx.arc(114, 25, 16, 0, Math.PI * 2);
    ctx.fill();

    // Cloud rim outline
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Golden lightning crest
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(68, 6);
    ctx.lineTo(60, 20);
    ctx.lineTo(69, 20);
    ctx.lineTo(63, 34);
    ctx.lineTo(76, 18);
    ctx.lineTo(68, 18);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 4. Moving Electric Platform
  if (!textures.exists('platform_electric')) {
    const canvas = textures.createCanvas('platform_electric', 130, 32);
    const ctx = canvas.getContext();

    // Metallic frame
    const grad = ctx.createLinearGradient(0, 0, 0, 32);
    grad.addColorStop(0, '#2e1065');
    grad.addColorStop(0.5, '#0f0529');
    grad.addColorStop(1, '#1e0538');
    ctx.fillStyle = grad;
    roundRect(ctx, 2, 2, 126, 28, 6);
    ctx.fill();

    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 28, 6);
    ctx.stroke();

    // Electric plasma conduit core
    const coreGrad = ctx.createLinearGradient(15, 0, 115, 0);
    coreGrad.addColorStop(0, 'rgba(192, 132, 252, 0.2)');
    coreGrad.addColorStop(0.5, '#e879f9');
    coreGrad.addColorStop(1, 'rgba(192, 132, 252, 0.2)');
    ctx.fillStyle = coreGrad;
    ctx.fillRect(16, 12, 98, 8);

    // Glowing core pulse wire
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(20, 16); ctx.lineTo(110, 16);
    ctx.stroke();

    canvas.refresh();
  }



  // 5. Golden Vinyl Collectible
  if (!textures.exists('item_vinyl')) {
    const canvas = textures.createCanvas('item_vinyl', 38, 38);
    const ctx = canvas.getContext();
    drawGoldenVinyl(ctx, 38);
    canvas.refresh();
  }



  // 6. Zeus Lightning Bolt Bonus
  if (!textures.exists('item_lightning')) {
    const canvas = textures.createCanvas('item_lightning', 36, 44);
    const ctx = canvas.getContext();

    // Golden outer glow
    const glowGrad = ctx.createRadialGradient(18, 22, 4, 18, 22, 18);
    glowGrad.addColorStop(0, 'rgba(253, 224, 71, 0.45)');
    glowGrad.addColorStop(1, 'rgba(253, 224, 71, 0)');
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.arc(18, 22, 18, 0, Math.PI * 2);
    ctx.fill();

    // Main bolt body
    const boltGrad = ctx.createLinearGradient(10, 2, 26, 42);
    boltGrad.addColorStop(0, '#ffffff');
    boltGrad.addColorStop(0.3, '#fef08a');
    boltGrad.addColorStop(0.7, '#facc15');
    boltGrad.addColorStop(1, '#ea580c');
    ctx.fillStyle = boltGrad;

    ctx.beginPath();
    ctx.moveTo(22, 2);
    ctx.lineTo(7, 21);
    ctx.lineTo(17, 21);
    ctx.lineTo(10, 42);
    ctx.lineTo(29, 17);
    ctx.lineTo(18, 17);
    ctx.closePath();
    ctx.fill();

    // Pure white hot spine
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(21, 4);
    ctx.lineTo(12, 20);
    ctx.lineTo(17, 20);
    ctx.lineTo(13, 38);
    ctx.stroke();

    canvas.refresh();
  }



  // 7. Hazard: Thunder Spikes
  if (!textures.exists('hazard_spike')) {
    const canvas = textures.createCanvas('hazard_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#fca5a5', '#ef4444', '#b91c1c'],
      ['#b91c1c', '#7f1d1d', '#450a0a'],
      '#ffffff',
      ['#334155', '#0f172a'],
      '#ef4444'
    );
    canvas.refresh();
  }



  // 8. Hazard: Plasma Orb
  if (!textures.exists('hazard_orb')) {
    const canvas = textures.createCanvas('hazard_orb', 38, 38);
    const ctx = canvas.getContext();
    drawPlasmaOrb(ctx, 38, '#f43f5e', '#be123c', 'rgba(159, 18, 57, 0.75)', '#fda4af');
    canvas.refresh();
  }



  // 8b. Tartarus Obsidian Platform
  if (!textures.exists('platform_tartarus')) {
    const canvas = textures.createCanvas('platform_tartarus', 120, 36);
    const ctx = canvas.getContext();

    // Dark obsidian crystal gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#2e0854');
    grad.addColorStop(0.2, '#18022e');
    grad.addColorStop(0.7, '#090014');
    grad.addColorStop(1, '#05000a');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Glowing electric violet running rail on surface
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#f5d0fe');
    rail.addColorStop(0.5, '#d946ef');
    rail.addColorStop(1, '#701a75');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous obsidian fracture / circuit lines across tiles
    ctx.strokeStyle = '#c026d3';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(30, 12); ctx.lineTo(60, 24); ctx.lineTo(95, 14); ctx.lineTo(120, 18);
    ctx.moveTo(15, 28); ctx.lineTo(45, 22); ctx.lineTo(80, 28); ctx.lineTo(110, 20);
    ctx.stroke();

    // Magenta neon core glow spots
    ctx.fillStyle = '#f43f5e';
    ctx.fillRect(30, 11, 4, 3);
    ctx.fillRect(60, 23, 4, 3);
    ctx.fillRect(95, 13, 4, 3);

    // Bottom dark rim
    ctx.fillStyle = '#1e0538';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }



  // 8c. Hazard: Tartarus Spikes
  if (!textures.exists('hazard_tartarus_spike')) {
    const canvas = textures.createCanvas('hazard_tartarus_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#f43f5e', '#d946ef', '#9333ea'],
      ['#9333ea', '#581c87', '#2e0854'],
      '#ffffff',
      ['#3b0764', '#090014'],
      '#d946ef'
    );
    canvas.refresh();
  }



  // 8d. Hazard: Laser Barrier
  if (!textures.exists('hazard_laser')) {
    const canvas = textures.createCanvas('hazard_laser', 24, 70);
    const ctx = canvas.getContext();

    // Heavy metallic emitters top and bottom with glowing status lights
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(2, 0, 20, 10);
    ctx.fillRect(2, 60, 20, 10);

    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(2, 0, 20, 10);
    ctx.strokeRect(2, 60, 20, 10);

    ctx.fillStyle = '#ef4444';
    ctx.fillRect(5, 3, 4, 4);
    ctx.fillRect(5, 63, 4, 4);

    // High-intensity laser beam core
    const beamGlow = ctx.createLinearGradient(6, 0, 18, 0);
    beamGlow.addColorStop(0, 'rgba(239, 68, 68, 0.2)');
    beamGlow.addColorStop(0.3, '#f43f5e');
    beamGlow.addColorStop(0.5, '#ffffff');
    beamGlow.addColorStop(0.7, '#f43f5e');
    beamGlow.addColorStop(1, 'rgba(239, 68, 68, 0.2)');
    ctx.fillStyle = beamGlow;
    ctx.fillRect(6, 10, 12, 50);

    canvas.refresh();
  }



  // 8e. Finish Portal: Stargate
  if (!textures.exists('portal_finish')) {
    const canvas = textures.createCanvas('portal_finish', 80, 140);
    const ctx = canvas.getContext();

    // Ornate Golden Stargate Columns
    const colGrad = ctx.createLinearGradient(0, 0, 80, 0);
    colGrad.addColorStop(0, '#78350f');
    colGrad.addColorStop(0.3, '#f59e0b');
    colGrad.addColorStop(0.5, '#fef08a');
    colGrad.addColorStop(0.7, '#f59e0b');
    colGrad.addColorStop(1, '#78350f');

    ctx.strokeStyle = colGrad;
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.arc(40, 48, 33, Math.PI, 0, false);
    ctx.lineTo(73, 136);
    ctx.lineTo(7, 136);
    ctx.closePath();
    ctx.stroke();

    // Swirling hyperspace portal vortex
    const radGrad = ctx.createRadialGradient(40, 58, 6, 40, 58, 36);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.35, '#38bdf8');
    radGrad.addColorStop(0.7, '#6366f1');
    radGrad.addColorStop(1, '#020617');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(40, 48, 28, Math.PI, 0, false);
    ctx.lineTo(68, 132);
    ctx.lineTo(12, 132);
    ctx.closePath();
    ctx.fill();

    // Central Zeus Lightning Portal Glyph
    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.moveTo(43, 30);
    ctx.lineTo(34, 52);
    ctx.lineTo(44, 52);
    ctx.lineTo(37, 76);
    ctx.lineTo(51, 48);
    ctx.lineTo(41, 48);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();

    canvas.refresh();
  }



  // 8f. STAGE 3: Hephaestus Cyber Forge Bronze Platform
  if (!textures.exists('platform_bronze')) {
    const canvas = textures.createCanvas('platform_bronze', 120, 36);
    const ctx = canvas.getContext();

    // Heavy industrial bronze alloy base
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#9a3412');
    grad.addColorStop(0.2, '#78350f');
    grad.addColorStop(0.7, '#451a03');
    grad.addColorStop(1, '#1c0a00');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Molten incandescent top guide rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#ffedd5');
    rail.addColorStop(0.5, '#ea580c');
    rail.addColorStop(1, '#7c2d12');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Forge plate joints & rivets across tiles
    ctx.strokeStyle = '#ea580c';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    ctx.moveTo(40, 4); ctx.lineTo(40, 33);
    ctx.moveTo(80, 4); ctx.lineTo(80, 33);
    ctx.stroke();

    // Golden steel rivets
    ctx.fillStyle = '#f59e0b';
    [10, 30, 50, 70, 90, 110].forEach(rx => {
      ctx.beginPath();
      ctx.arc(rx, 10, 2, 0, Math.PI * 2);
      ctx.arc(rx, 26, 2, 0, Math.PI * 2);
      ctx.fill();
    });

    // Dark bottom trim
    ctx.fillStyle = '#290e00';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }



  // 8g. STAGE 3: Floating Magma Platform
  if (!textures.exists('platform_magma')) {
    const canvas = textures.createCanvas('platform_magma', 130, 34);
    const ctx = canvas.getContext();

    // Dark crust
    ctx.fillStyle = '#1c0a00';
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#f97316';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    // Molten magma core
    const magmaGrad = ctx.createLinearGradient(15, 0, 115, 0);
    magmaGrad.addColorStop(0, '#ea580c');
    magmaGrad.addColorStop(0.5, '#fde047');
    magmaGrad.addColorStop(1, '#ea580c');
    ctx.fillStyle = magmaGrad;
    ctx.fillRect(14, 11, 102, 12);

    // Glowing core spine
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(20, 17); ctx.lineTo(110, 17);
    ctx.stroke();

    canvas.refresh();
  }



  // 8h. STAGE 3: Hazard Forge Magma Spikes
  if (!textures.exists('hazard_forge_spike')) {
    const canvas = textures.createCanvas('hazard_forge_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#fef08a', '#f59e0b', '#ea580c'],
      ['#ea580c', '#9a3412', '#431407'],
      '#ffffff',
      ['#451a03', '#1c0a00'],
      '#f97316'
    );
    canvas.refresh();
  }



  // 8i. STAGE 3: Hazard Rotating Saw Blade
  if (!textures.exists('hazard_saw')) {
    const canvas = textures.createCanvas('hazard_saw', 44, 44);
    const ctx = canvas.getContext();
    drawSawBlade(ctx, 44, '#475569', '#ea580c', '#f97316', '#f59e0b');
    canvas.refresh();
  }



  // 8j. STAGE 4: Cosmic Olympus Crystal Platform
  if (!textures.exists('platform_cosmic')) {
    const canvas = textures.createCanvas('platform_cosmic', 120, 36);
    const ctx = canvas.getContext();

    // Deep cosmic space gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#312e81');
    grad.addColorStop(0.25, '#1e1b4b');
    grad.addColorStop(0.7, '#0f172a');
    grad.addColorStop(1, '#020617');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Cyan cosmic running rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#e0f2fe');
    rail.addColorStop(0.5, '#38bdf8');
    rail.addColorStop(1, '#0369a1');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Starlight lattice grid
    ctx.strokeStyle = 'rgba(129, 140, 248, 0.45)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    for (let x = 20; x < 120; x += 40) {
      ctx.moveTo(x - 10, 18); ctx.lineTo(x, 10); ctx.lineTo(x + 10, 18); ctx.lineTo(x, 26); ctx.closePath();
    }
    ctx.stroke();

    // Golden celestial stars
    ctx.fillStyle = '#facc15';
    [20, 60, 100].forEach(sx => {
      ctx.beginPath();
      ctx.arc(sx, 18, 2, 0, Math.PI * 2);
      ctx.fill();
    });

    // Bottom dark rim
    ctx.fillStyle = '#020617';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }



  // 8k. STAGE 4: Moving Cosmic Nebula Platform
  if (!textures.exists('platform_nebula')) {
    const canvas = textures.createCanvas('platform_nebula', 130, 36);
    const ctx = canvas.getContext();

    // Violet nebula clouds
    ctx.fillStyle = 'rgba(126, 34, 206, 0.8)';
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.fill();

    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.stroke();

    // Nebula core starlight band
    const coreGrad = ctx.createLinearGradient(15, 0, 115, 0);
    coreGrad.addColorStop(0, 'rgba(56, 189, 248, 0.3)');
    coreGrad.addColorStop(0.5, '#f472b6');
    coreGrad.addColorStop(1, 'rgba(56, 189, 248, 0.3)');
    ctx.fillStyle = coreGrad;
    ctx.fillRect(16, 13, 98, 10);

    // Sparks
    ctx.fillStyle = '#ffffff';
    [35, 65, 95].forEach(sx => {
      ctx.beginPath();
      ctx.arc(sx, 18, 2, 0, Math.PI * 2);
      ctx.fill();
    });

    canvas.refresh();
  }



  // 8l. STAGE 4: Hazard Cosmic Prism Spikes
  if (!textures.exists('hazard_cosmic_spike')) {
    const canvas = textures.createCanvas('hazard_cosmic_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#c084fc', '#818cf8', '#38bdf8'],
      ['#6366f1', '#312e81', '#0f172a'],
      '#ffffff',
      ['#1e1b4b', '#020617'],
      '#38bdf8'
    );
    canvas.refresh();
  }



  // 8m. STAGE 4: Hazard Pulsar Supernova Orb
  if (!textures.exists('hazard_supernova')) {
    const canvas = textures.createCanvas('hazard_supernova', 40, 40);
    const ctx = canvas.getContext();
    drawPlasmaOrb(ctx, 40, '#38bdf8', '#818cf8', 'rgba(129, 140, 248, 0.75)', '#e0f2fe');
    canvas.refresh();
  }

  // =========================================================================
  // STAGE 5: ATLANTIS NEON ABYSS TEXTURES
  // =========================================================================
  // 8n. STAGE 5: Bioluminescent Coral Platform
  if (!textures.exists('platform_coral')) {
    const canvas = textures.createCanvas('platform_coral', 120, 36);
    const ctx = canvas.getContext();

    // Deep abyss ocean gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#155e75');
    grad.addColorStop(0.2, '#0e7490');
    grad.addColorStop(0.7, '#083344');
    grad.addColorStop(1, '#021824');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Glowing cyan running rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#cffafe');
    rail.addColorStop(0.5, '#22d3ee');
    rail.addColorStop(1, '#0891b2');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous aquatic wave fretwork
    ctx.strokeStyle = '#06b6d4';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    for (let x = 0; x < 120; x += 30) {
      ctx.moveTo(x, 18); ctx.quadraticCurveTo(x + 15, 8, x + 30, 18);
      ctx.moveTo(x, 18); ctx.quadraticCurveTo(x + 15, 28, x + 30, 18);
    }
    ctx.stroke();

    // Bioluminescent coral nodes
    ctx.fillStyle = '#67e8f9';
    [15, 45, 75, 105].forEach(cx => {
      ctx.beginPath();
      ctx.arc(cx, 18, 3, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.fillStyle = '#021824';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8o. STAGE 5: Moving Hydro-Beam Platform
  if (!textures.exists('platform_hydro')) {
    const canvas = textures.createCanvas('platform_hydro', 130, 34);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#083344';
    roundRect(ctx, 2, 2, 126, 30, 8);
    ctx.fill();

    ctx.strokeStyle = '#22d3ee';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 8);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(6, 182, 212, 0.3)');
    grad.addColorStop(0.5, 'rgba(34, 211, 238, 0.95)');
    grad.addColorStop(1, 'rgba(6, 182, 212, 0.3)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 12, 98, 10);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(25, 16, 80, 2);

    canvas.refresh();
  }

  // 8p. STAGE 5: Sea-Crystal Coral Spikes
  if (!textures.exists('hazard_coral_spike')) {
    const canvas = textures.createCanvas('hazard_coral_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#a5f3fc', '#22d3ee', '#0891b2'],
      ['#0891b2', '#0e7490', '#083344'],
      '#ffffff',
      ['#0e7490', '#042f2e'],
      '#22d3ee'
    );
    canvas.refresh();
  }

  // 8q. STAGE 5: Hazard Bioluminescent Jellyfish
  if (!textures.exists('hazard_jellyfish')) {
    const canvas = textures.createCanvas('hazard_jellyfish', 42, 42);
    const ctx = canvas.getContext();

    // Dome bell
    const radGrad = ctx.createRadialGradient(21, 16, 2, 21, 16, 16);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#22d3ee');
    radGrad.addColorStop(0.8, '#0891b2');
    radGrad.addColorStop(1, '#164e63');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(21, 18, 16, Math.PI, 0, false);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#cffafe';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Shock tentacles
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(10, 18); ctx.lineTo(8, 36);
    ctx.moveTo(16, 18); ctx.lineTo(15, 39);
    ctx.moveTo(21, 18); ctx.lineTo(21, 40);
    ctx.moveTo(26, 18); ctx.lineTo(27, 39);
    ctx.moveTo(32, 18); ctx.lineTo(34, 36);
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 6: DIONYSUS NEON RAVE TEXTURES
  // =========================================================================
  // 8r. STAGE 6: Cyber Grape Vineyard Platform
  if (!textures.exists('platform_grape')) {
    const canvas = textures.createCanvas('platform_grape', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#581c87');
    grad.addColorStop(0.2, '#3b0764');
    grad.addColorStop(0.7, '#1e0533');
    grad.addColorStop(1, '#0d0017');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Glowing gold & magenta neon rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#fef08a');
    rail.addColorStop(0.5, '#facc15');
    rail.addColorStop(1, '#d946ef');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous synthwave zigzag fretwork
    ctx.strokeStyle = '#e879f9';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18);
    for (let x = 0; x < 120; x += 20) {
      ctx.lineTo(x + 10, 10);
      ctx.lineTo(x + 20, 18);
    }
    ctx.stroke();

    // Neon berries
    ctx.fillStyle = '#facc15';
    [10, 30, 50, 70, 90, 110].forEach(bx => {
      ctx.beginPath();
      ctx.arc(bx, 10, 2.5, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.fillStyle = '#0d0017';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8s. STAGE 6: Moving Golden Rave Stage Platform
  if (!textures.exists('platform_goldrave')) {
    const canvas = textures.createCanvas('platform_goldrave', 130, 36);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#3b0764';
    roundRect(ctx, 2, 2, 126, 32, 6);
    ctx.fill();

    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 32, 6);
    ctx.stroke();

    // Sound equalizer bars
    const eqColors = ['#f43f5e', '#ec4899', '#d946ef', '#a855f7', '#3b82f6'];
    for (let x = 16; x < 114; x += 10) {
      const h = 4 + ((x * 7) % 18);
      ctx.fillStyle = eqColors[Math.floor(x / 20) % eqColors.length];
      ctx.fillRect(x, 26 - h, 6, h);
    }

    canvas.refresh();
  }

  // 8t. STAGE 6: Neon Rave Party Spikes
  if (!textures.exists('hazard_rave_spike')) {
    const canvas = textures.createCanvas('hazard_rave_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#fde047', '#f43f5e', '#d946ef'],
      ['#d946ef', '#9333ea', '#3b0764'],
      '#ffffff',
      ['#3b0764', '#1e0533'],
      '#fde047'
    );
    canvas.refresh();
  }

  // 8u. STAGE 6: Hazard Bass Soundwave Speaker
  if (!textures.exists('hazard_speaker')) {
    const canvas = textures.createCanvas('hazard_speaker', 44, 44);
    const ctx = canvas.getContext();

    // Outer dark housing
    ctx.fillStyle = '#1e1b4b';
    roundRect(ctx, 2, 2, 40, 40, 8);
    ctx.fill();
    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 40, 40, 8);
    ctx.stroke();

    // Speaker cone with soundwave glow
    const radGrad = ctx.createRadialGradient(22, 22, 2, 22, 22, 15);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.4, '#e879f9');
    radGrad.addColorStop(0.8, '#a21caf');
    radGrad.addColorStop(1, '#4c0519');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 14, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.stroke();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 7: ARES ORBITAL WAR CITADEL TEXTURES
  // =========================================================================
  // 8v. STAGE 7: Heavy Titanium Armor Platform
  if (!textures.exists('platform_titanium')) {
    const canvas = textures.createCanvas('platform_titanium', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#3f3f46');
    grad.addColorStop(0.2, '#27272a');
    grad.addColorStop(0.7, '#18181b');
    grad.addColorStop(1, '#09090b');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Tactical combat crimson running rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#fca5a5');
    rail.addColorStop(0.5, '#ef4444');
    rail.addColorStop(1, '#991b1b');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Hazard warning stripes along center
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    ctx.stroke();

    ctx.fillStyle = '#ef4444';
    [10, 40, 70, 100].forEach(sx => {
      ctx.fillRect(sx, 12, 12, 12);
    });

    ctx.fillStyle = '#09090b';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8w. STAGE 7: Moving Plasma Conduit Platform
  if (!textures.exists('platform_plasma')) {
    const canvas = textures.createCanvas('platform_plasma', 130, 34);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#18181b';
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(239, 68, 68, 0.4)');
    grad.addColorStop(0.5, 'rgba(249, 115, 22, 0.95)');
    grad.addColorStop(1, 'rgba(239, 68, 68, 0.4)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 11, 98, 12);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(25, 16, 80, 2);

    canvas.refresh();
  }

  // 8x. STAGE 7: Serrated Combat Titanium Spikes
  if (!textures.exists('hazard_ares_spike')) {
    const canvas = textures.createCanvas('hazard_ares_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#fca5a5', '#ef4444', '#b91c1c'],
      ['#b91c1c', '#7f1d1d', '#450a0a'],
      '#ffffff',
      ['#27272a', '#09090b'],
      '#ef4444'
    );
    canvas.refresh();
  }

  // 8y. STAGE 7: Hazard Orbital Magnetic Mine
  if (!textures.exists('hazard_mine')) {
    const canvas = textures.createCanvas('hazard_mine', 44, 44);
    const ctx = canvas.getContext();

    const radGrad = ctx.createRadialGradient(22, 22, 3, 22, 22, 18);
    radGrad.addColorStop(0, '#ef4444');
    radGrad.addColorStop(0.4, '#991b1b');
    radGrad.addColorStop(0.8, '#27272a');
    radGrad.addColorStop(1, '#09090b');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 16, 0, Math.PI * 2);
    ctx.fill();

    // Spikes around mine
    ctx.strokeStyle = '#f87171';
    ctx.lineWidth = 3;
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI * 2) / 8;
      ctx.beginPath();
      ctx.moveTo(22 + Math.cos(angle) * 15, 22 + Math.sin(angle) * 15);
      ctx.lineTo(22 + Math.cos(angle) * 21, 22 + Math.sin(angle) * 21);
      ctx.stroke();
    }

    // Glowing blinking core
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(22, 22, 4, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 8: KRONOS CHRONO-SINGULARITY TEXTURES
  // =========================================================================
  // 8z. STAGE 8: Golden Clockwork Platform
  if (!textures.exists('platform_chronos')) {
    const canvas = textures.createCanvas('platform_chronos', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#b45309');
    grad.addColorStop(0.2, '#78350f');
    grad.addColorStop(0.7, '#451a03');
    grad.addColorStop(1, '#1e0538');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Glowing chronal gold running rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#fef08a');
    rail.addColorStop(0.5, '#fde047');
    rail.addColorStop(1, '#b45309');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous clockwork gear rack teeth
    ctx.fillStyle = '#fde047';
    for (let x = 8; x < 120; x += 16) {
      ctx.fillRect(x, 14, 8, 8);
    }
    ctx.strokeStyle = '#b45309';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 14, 120, 8);

    ctx.fillStyle = '#1e0538';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8aa. STAGE 8: Moving Spacetime Rift Platform
  if (!textures.exists('platform_rift')) {
    const canvas = textures.createCanvas('platform_rift', 130, 36);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#1e1b4b';
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.fill();

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(168, 85, 247, 0.7)');
    grad.addColorStop(0.5, 'rgba(253, 224, 71, 0.95)');
    grad.addColorStop(1, 'rgba(56, 189, 248, 0.7)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 12, 98, 12);

    canvas.refresh();
  }

  // 8ab. STAGE 8: Temporal Shard Spikes
  if (!textures.exists('hazard_chronos_spike')) {
    const canvas = textures.createCanvas('hazard_chronos_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#fef08a', '#facc15', '#a855f7'],
      ['#a855f7', '#6b21a8', '#31104b'],
      '#ffffff',
      ['#451a03', '#1e0538'],
      '#fde047'
    );
    canvas.refresh();
  }

  // =========================================================================
  // STAGE 9: RIVER STYX SPECTRAL RUN TEXTURES
  // =========================================================================
  // 8ad. STAGE 9: Spectral Bone-Jade Stone Platform
  if (!textures.exists('platform_styx')) {
    const canvas = textures.createCanvas('platform_styx', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#065f46');
    grad.addColorStop(0.2, '#047857');
    grad.addColorStop(0.7, '#064e3b');
    grad.addColorStop(1, '#022c22');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Ethereal emerald ghost-fire rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#a7f3d0');
    rail.addColorStop(0.5, '#34d399');
    rail.addColorStop(1, '#059669');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous ghostly rune glyphs
    ctx.strokeStyle = '#6ee7b7';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    for (let x = 15; x < 120; x += 30) {
      ctx.moveTo(x - 6, 18); ctx.lineTo(x, 10); ctx.lineTo(x + 6, 18); ctx.lineTo(x, 26); ctx.closePath();
    }
    ctx.stroke();

    ctx.fillStyle = '#022c22';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8ae. STAGE 9: Moving Ghost Barge Ferry Platform
  if (!textures.exists('platform_ferry')) {
    const canvas = textures.createCanvas('platform_ferry', 130, 34);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#022c22';
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(6, 78, 59, 0.7)');
    grad.addColorStop(0.5, 'rgba(16, 185, 129, 0.95)');
    grad.addColorStop(1, 'rgba(6, 78, 59, 0.7)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 11, 98, 12);

    canvas.refresh();
  }

  // 8af. STAGE 9: Ghost-Fire Jade Crystal Spikes
  if (!textures.exists('hazard_styx_spike')) {
    const canvas = textures.createCanvas('hazard_styx_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#a7f3d0', '#34d399', '#059669'],
      ['#059669', '#064e3b', '#022c22'],
      '#ffffff',
      ['#064e3b', '#011c15'],
      '#34d399'
    );
    canvas.refresh();
  }

  // 8ag. STAGE 9: Hazard Gliding Ghost Specter
  if (!textures.exists('hazard_ghost')) {
    const canvas = textures.createCanvas('hazard_ghost', 44, 44);
    const ctx = canvas.getContext();

    // Floating Wraith Skull
    const radGrad = ctx.createRadialGradient(22, 18, 2, 22, 18, 16);
    radGrad.addColorStop(0, '#ffffff');
    radGrad.addColorStop(0.3, '#34d399');
    radGrad.addColorStop(0.7, '#059669');
    radGrad.addColorStop(1, '#022c22');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 18, 14, 0, Math.PI * 2);
    ctx.fill();

    // Ghost trails
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.moveTo(10, 22); ctx.lineTo(8, 40); ctx.lineTo(16, 26);
    ctx.lineTo(22, 42); ctx.lineTo(28, 26); ctx.lineTo(36, 40); ctx.lineTo(34, 22);
    ctx.fill();

    // Glowing eyes
    ctx.fillStyle = '#01140e';
    ctx.beginPath();
    ctx.arc(17, 16, 3, 0, Math.PI * 2);
    ctx.arc(27, 16, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#a7f3d0';
    ctx.beginPath();
    ctx.arc(17, 16, 1.5, 0, Math.PI * 2);
    ctx.arc(27, 16, 1.5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }

  // =========================================================================
  // STAGE 10: APOLLO SOLAR HYPER-DRIVE TEXTURES
  // =========================================================================
  // 8ah. STAGE 10: Blazing Sunfire Alloy Platform
  if (!textures.exists('platform_solar')) {
    const canvas = textures.createCanvas('platform_solar', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 0, 36);
    grad.addColorStop(0, '#ea580c');
    grad.addColorStop(0.2, '#c2410c');
    grad.addColorStop(0.7, '#7c2d12');
    grad.addColorStop(1, '#270802');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Incandescent solar running rail
    const rail = ctx.createLinearGradient(0, 0, 0, 4);
    rail.addColorStop(0, '#ffffff');
    rail.addColorStop(0.5, '#fde047');
    rail.addColorStop(1, '#ea580c');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Continuous solar prominence lattice
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, 18); ctx.lineTo(120, 18);
    for (let x = 10; x < 120; x += 24) {
      ctx.moveTo(x, 18); ctx.lineTo(x + 12, 10); ctx.lineTo(x + 24, 18); ctx.lineTo(x + 12, 26); ctx.closePath();
    }
    ctx.stroke();

    ctx.fillStyle = '#270802';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8ai. STAGE 10: Moving Fusion Plasma Beam Platform
  if (!textures.exists('platform_sunspot')) {
    const canvas = textures.createCanvas('platform_sunspot', 130, 34);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#431407';
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.fill();

    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 30, 6);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(234, 88, 12, 0.5)');
    grad.addColorStop(0.5, 'rgba(254, 240, 138, 0.95)');
    grad.addColorStop(1, 'rgba(234, 88, 12, 0.5)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 11, 98, 12);

    canvas.refresh();
  }

  // 8aj. STAGE 10: Prominence Solar Plasma Spikes
  if (!textures.exists('hazard_solar_spike')) {
    const canvas = textures.createCanvas('hazard_solar_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#ffffff', '#fde047', '#f97316'],
      ['#f97316', '#ea580c', '#431407'],
      '#ffffff',
      ['#7c2d12', '#270802'],
      '#fde047'
    );
    canvas.refresh();
  }

  // 8ak. STAGE 10: Hazard Spinning Solar Flare Orb
  if (!textures.exists('hazard_sunflare')) {
    const canvas = textures.createCanvas('hazard_sunflare', 44, 44);
    const ctx = canvas.getContext();
    drawPlasmaOrb(ctx, 44, '#ffffff', '#fde047', 'rgba(234, 88, 12, 0.85)', '#fef08a');
    canvas.refresh();
  }

  // =========================================================================
  // STAGE 11: CHAOS PRIMORDIAL GENESIS TEXTURES
  // =========================================================================
  // 8al. STAGE 11: Quantum Antimatter Shard Platform
  if (!textures.exists('platform_chaos')) {
    const canvas = textures.createCanvas('platform_chaos', 120, 36);
    const ctx = canvas.getContext();

    const grad = ctx.createLinearGradient(0, 0, 120, 36);
    grad.addColorStop(0, '#ec4899');
    grad.addColorStop(0.35, '#8b5cf6');
    grad.addColorStop(0.7, '#3b82f6');
    grad.addColorStop(1, '#020617');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 120, 36);

    // Prismatic stellar running rail
    const rail = ctx.createLinearGradient(0, 0, 120, 0);
    rail.addColorStop(0, '#ffffff');
    rail.addColorStop(0.3, '#f472b6');
    rail.addColorStop(0.6, '#a78bfa');
    rail.addColorStop(1, '#38bdf8');
    ctx.fillStyle = rail;
    ctx.fillRect(0, 0, 120, 4);

    // Prismatic geometric crystal inlays
    ctx.fillStyle = '#facc15';
    for (let x = 15; x < 120; x += 30) {
      ctx.beginPath();
      ctx.moveTo(x, 10); ctx.lineTo(x + 7, 18); ctx.lineTo(x, 26); ctx.lineTo(x - 7, 18);
      ctx.closePath();
      ctx.fill();
    }

    ctx.fillStyle = '#020617';
    ctx.fillRect(0, 33, 120, 3);

    canvas.refresh();
  }

  // 8am. STAGE 11: Moving Cosmic String Ribbon Platform
  if (!textures.exists('platform_genesis')) {
    const canvas = textures.createCanvas('platform_genesis', 130, 36);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#020617';
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    roundRect(ctx, 2, 2, 126, 32, 8);
    ctx.stroke();

    const grad = ctx.createLinearGradient(0, 0, 130, 0);
    grad.addColorStop(0, 'rgba(236, 72, 153, 0.7)');
    grad.addColorStop(0.5, 'rgba(250, 204, 21, 0.95)');
    grad.addColorStop(1, 'rgba(59, 130, 246, 0.7)');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 12, 98, 12);

    canvas.refresh();
  }

  // 8an. STAGE 11: Reality-Bending Prismatic Spikes
  if (!textures.exists('hazard_chaos_spike')) {
    const canvas = textures.createCanvas('hazard_chaos_spike', 36, 40);
    const ctx = canvas.getContext();
    draw3DFacetedSpike(
      ctx,
      36,
      40,
      ['#ffffff', '#f43f5e', '#8b5cf6'],
      ['#8b5cf6', '#3b82f6', '#020617'],
      '#ffffff',
      ['#1e1b4b', '#020617'],
      '#38bdf8'
    );
    canvas.refresh();
  }

  // 8ao. STAGE 11: Hazard Antimatter Singularity Core
  if (!textures.exists('hazard_antimatter')) {
    const canvas = textures.createCanvas('hazard_antimatter', 44, 44);
    const ctx = canvas.getContext();

    // Event horizon outer ring
    const radGrad = ctx.createRadialGradient(22, 22, 6, 22, 22, 20);
    radGrad.addColorStop(0, '#000000');
    radGrad.addColorStop(0.4, '#1e1b4b');
    radGrad.addColorStop(0.7, '#ec4899');
    radGrad.addColorStop(1, '#38bdf8');

    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(22, 22, 18, 0, Math.PI * 2);
    ctx.fill();

    // Black Hole Pure Darkness center
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.arc(22, 22, 9, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.stroke();

    canvas.refresh();
  }



  // 9. Particle: Spark / Star
  if (!textures.exists('particle_star')) {
    const canvas = textures.createCanvas('particle_star', 16, 16);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#fde047';
    ctx.beginPath();
    ctx.arc(8, 8, 5, 0, Math.PI * 2);
    ctx.fill();

    canvas.refresh();
  }



  // 10. Particle: Music Note
  if (!textures.exists('particle_note')) {
    const canvas = textures.createCanvas('particle_note', 24, 24);
    const ctx = canvas.getContext();

    ctx.fillStyle = '#67e8f9';
    ctx.font = 'bold 20px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('♪', 12, 12);

    canvas.refresh();
  }



  // 11. UI Button: Anti-Gravity Button
  if (!textures.exists('btn_antigravity')) {
    const canvas = textures.createCanvas('btn_antigravity', 120, 120);
    const ctx = canvas.getContext();

    // Outer glow ring
    const radGrad = ctx.createRadialGradient(60, 60, 42, 60, 60, 59);
    radGrad.addColorStop(0, 'rgba(245, 158, 11, 0.55)');
    radGrad.addColorStop(0.6, 'rgba(239, 68, 68, 0.45)');
    radGrad.addColorStop(1, 'rgba(124, 58, 237, 0)');
    ctx.fillStyle = radGrad;
    ctx.beginPath();
    ctx.arc(60, 60, 59, 0, Math.PI * 2);
    ctx.fill();

    // Heavy gold bevel ring
    const ringGrad = ctx.createLinearGradient(15, 15, 105, 105);
    ringGrad.addColorStop(0, '#fde047');
    ringGrad.addColorStop(0.5, '#f59e0b');
    ringGrad.addColorStop(1, '#b45309');
    ctx.fillStyle = ringGrad;
    ctx.beginPath();
    ctx.arc(60, 60, 52, 0, Math.PI * 2);
    ctx.fill();

    // Inner dark titanium disc
    const innerDisc = ctx.createRadialGradient(56, 56, 5, 60, 60, 46);
    innerDisc.addColorStop(0, '#1e293b');
    innerDisc.addColorStop(0.7, '#0f172a');
    innerDisc.addColorStop(1, '#020617');
    ctx.fillStyle = innerDisc;
    ctx.beginPath();
    ctx.arc(60, 60, 46, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#facc15';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(60, 60, 46, 0, Math.PI * 2);
    ctx.stroke();

    // Inverted Chevrons (Up & Down Anti-Grav arrows)
    // Up arrow
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(60, 22);
    ctx.lineTo(42, 39);
    ctx.lineTo(52, 39);
    ctx.lineTo(52, 44);
    ctx.lineTo(68, 44);
    ctx.lineTo(68, 39);
    ctx.lineTo(78, 39);
    ctx.closePath();
    ctx.fill();

    // Down arrow
    ctx.beginPath();
    ctx.moveTo(60, 98);
    ctx.lineTo(42, 81);
    ctx.lineTo(52, 81);
    ctx.lineTo(52, 76);
    ctx.lineTo(68, 76);
    ctx.lineTo(68, 81);
    ctx.lineTo(78, 81);
    ctx.closePath();
    ctx.fill();

    // Center Zeus Lightning Emblem
    const boltGrad = ctx.createLinearGradient(50, 44, 70, 76);
    boltGrad.addColorStop(0, '#ffffff');
    boltGrad.addColorStop(0.4, '#fde047');
    boltGrad.addColorStop(1, '#f59e0b');
    ctx.fillStyle = boltGrad;
    ctx.beginPath();
    ctx.moveTo(64, 42);
    ctx.lineTo(50, 61);
    ctx.lineTo(61, 61);
    ctx.lineTo(55, 78);
    ctx.lineTo(71, 57);
    ctx.lineTo(60, 57);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.stroke();

    canvas.refresh();
  }



  // 12. UI Button: Left Arrow
  if (!textures.exists('btn_arrow_left')) {
    const canvas = textures.createCanvas('btn_arrow_left', 80, 80);
    const ctx = canvas.getContext();

    // Outer glow
    const grad = ctx.createRadialGradient(40, 40, 28, 40, 40, 39);
    grad.addColorStop(0, 'rgba(56, 189, 248, 0.4)');
    grad.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(40, 40, 39, 0, Math.PI * 2);
    ctx.fill();

    // Disc
    ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.stroke();

    // Crisp Arrow
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.moveTo(25, 40);
    ctx.lineTo(50, 24);
    ctx.lineTo(50, 33);
    ctx.lineTo(57, 33);
    ctx.lineTo(57, 47);
    ctx.lineTo(50, 47);
    ctx.lineTo(50, 56);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }



  // 13. UI Button: Right Arrow
  if (!textures.exists('btn_arrow_right')) {
    const canvas = textures.createCanvas('btn_arrow_right', 80, 80);
    const ctx = canvas.getContext();

    // Outer glow
    const grad = ctx.createRadialGradient(40, 40, 28, 40, 40, 39);
    grad.addColorStop(0, 'rgba(56, 189, 248, 0.4)');
    grad.addColorStop(1, 'rgba(56, 189, 248, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(40, 40, 39, 0, Math.PI * 2);
    ctx.fill();

    // Disc
    ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(40, 40, 34, 0, Math.PI * 2);
    ctx.stroke();

    // Crisp Arrow
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.moveTo(55, 40);
    ctx.lineTo(30, 24);
    ctx.lineTo(30, 33);
    ctx.lineTo(23, 33);
    ctx.lineTo(23, 47);
    ctx.lineTo(30, 47);
    ctx.lineTo(30, 56);
    ctx.closePath();
    ctx.fill();

    canvas.refresh();
  }
}

/**
 * Detailed procedural rendering for Zelvis (King of Rock 'n Olympus)
 * Supports: idle, run_1..6, slide (rockstar knee slide), skid (braking), and flip (mid-air)
 */
function drawDetailedZelvis(ctx, key) {
  ctx.save();
  ctx.clearRect(0, 0, 80, 96);

  const isSlide = key === 'zelvis_slide';
  const isSkid = key === 'zelvis_skid';
  const isFlip = key === 'zelvis_flip';
  const isIdle = key === 'zelvis_idle';

  let runFrame = 0;
  if (key.startsWith('zelvis_run_')) {
    runFrame = parseInt(key.replace('zelvis_run_', ''), 10);
  }

  // 1. CAPE RENDERING (Dynamic flowing red velvet cape with shading and gold trim)
  ctx.save();
  drawDetailedCape(ctx, key, runFrame, isSlide, isSkid, isFlip);
  ctx.restore();

  // 2. BODY & LIMBS
  if (isSlide) {
    // === ROCKSTAR KNEE SLIDE POSE ===
    // Body reclined backward at 45 degrees, sliding horizontally on knees
    ctx.save();
    // Sliding legs on knees
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    // Both legs tucked under, sliding on shins
    ctx.beginPath();
    ctx.moveTo(35, 62);
    ctx.lineTo(15, 84);
    ctx.lineTo(40, 86);
    ctx.lineTo(52, 70);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Golden boots tucked back
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(8, 80, 16, 8);
    // Gold lightning trim on legs
    ctx.strokeStyle = '#fbbf24';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(25, 72);
    ctx.lineTo(45, 76);
    ctx.stroke();

    // Reclined torso
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(34, 58);
    ctx.lineTo(58, 48);
    ctx.lineTo(66, 62);
    ctx.lineTo(42, 72);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Gold belt
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(38, 64, 16, 6);
    ctx.fillStyle = '#fde047';
    ctx.fillRect(44, 63, 6, 8);

    // Left arm extended back in rock sign
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(24, 50, 14, 6);

    // Right arm reaching forward with golden microphone
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(56, 52);
    ctx.lineTo(72, 44);
    ctx.lineTo(70, 50);
    ctx.lineTo(54, 58);
    ctx.closePath();
    ctx.fill();

    // Microphone
    drawMicrophone(ctx, 74, 42, -0.2);

    // Head tilted up triumphantly
    drawHead(ctx, 58, 32, true);
    ctx.restore();

  } else if (isSkid) {
    // === ROCKSTAR BRAKING / SKID POSE ===
    // 3/4 turn, heels dug in, arms stabilizing, torso braced
    ctx.save();
    // Braced flared legs
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    // Left leg back bracing
    ctx.beginPath();
    ctx.moveTo(34, 54);
    ctx.lineTo(18, 86);
    ctx.lineTo(28, 88);
    ctx.lineTo(40, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Right leg forward digging heel
    ctx.beginPath();
    ctx.moveTo(38, 54);
    ctx.lineTo(52, 85);
    ctx.lineTo(62, 83);
    ctx.lineTo(46, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Gold boots
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(14, 84, 16, 8);
    ctx.fillRect(52, 81, 16, 8);

    // Gold lightning on trousers
    drawTrousersLightning(ctx, 22, 68, 54, 68);

    // Torso leaned back against momentum
    drawTorso(ctx, 30, 34, 22, 24);

    // Belt
    drawBelt(ctx, 30, 54, 22);

    // Arms spread wide for balance
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(18, 38, 14, 6); // left arm back
    ctx.fillRect(48, 38, 16, 6); // right arm forward with mic
    drawMicrophone(ctx, 66, 36, 0.3);

    // Head focused ahead
    drawHead(ctx, 41, 24, false);
    ctx.restore();

  } else if (isFlip) {
    // === ANTI-GRAVITY MIDAIR FLIP POSE ===
    ctx.save();
    // Tucked athletic legs
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(34, 52);
    ctx.lineTo(24, 70);
    ctx.lineTo(34, 74);
    ctx.lineTo(40, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(42, 52);
    ctx.lineTo(52, 70);
    ctx.lineTo(44, 74);
    ctx.lineTo(36, 56);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Boots
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(22, 68, 14, 7);
    ctx.fillRect(42, 68, 14, 7);

    // Torso
    drawTorso(ctx, 30, 32, 22, 24);
    drawBelt(ctx, 30, 52, 22);

    // Arms outstretched heroically
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(14, 34, 16, 6);
    ctx.fillRect(50, 32, 16, 6);
    drawMicrophone(ctx, 68, 30, -0.4);

    drawHead(ctx, 41, 22, false);

    // Zeus Electric Aura circles
    ctx.strokeStyle = '#fde047';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(41, 46, 34, 0, Math.PI * 2);
    ctx.stroke();

    ctx.restore();

  } else {
    // === RUN CYCLE (6 FRAMES) OR IDLE ===
    ctx.save();

    // Legs position per run frame
    drawRunLegs(ctx, runFrame, isIdle);

    // Torso with slight bob
    const bob = (runFrame === 2 || runFrame === 5) ? 2 : 0;
    drawTorso(ctx, 31, 33 + bob, 22, 24);
    drawBelt(ctx, 31, 53 + bob, 22);

    // Arms & Golden Microphone swinging with stride
    drawRunArms(ctx, runFrame, isIdle, bob);

    // Head
    drawHead(ctx, 42, 23 + bob, false);

    ctx.restore();
  }

  ctx.restore();
}

/**
 * Dynamic Flowing Cape with Satin Folds & Shading
 */
function drawDetailedCape(ctx, key, runFrame, isSlide, isSkid, isFlip) {
  // Cape gradient
  const grad = ctx.createLinearGradient(10, 20, 70, 80);
  grad.addColorStop(0, '#ef4444');
  grad.addColorStop(0.5, '#dc2626');
  grad.addColorStop(1, '#991b1b');
  ctx.fillStyle = grad;

  ctx.beginPath();
  if (isSlide) {
    // Horizontal trailing cape
    ctx.moveTo(35, 52);
    ctx.quadraticCurveTo(15, 48, 2, 54);
    ctx.lineTo(4, 68);
    ctx.quadraticCurveTo(20, 64, 40, 68);
  } else if (isSkid) {
    // Cape whipping around from braking
    ctx.moveTo(36, 34);
    ctx.quadraticCurveTo(10, 20, 8, 48);
    ctx.lineTo(20, 66);
    ctx.lineTo(34, 48);
  } else if (isFlip) {
    // Wide billowing circular cape
    ctx.moveTo(30, 32);
    ctx.quadraticCurveTo(4, 38, 6, 75);
    ctx.lineTo(26, 70);
    ctx.lineTo(36, 46);
  } else {
    // Dynamic run frames (billowing up and down)
    const wave = Math.sin((runFrame || 1) * Math.PI / 3) * 8;
    ctx.moveTo(30, 34);
    ctx.quadraticCurveTo(10 - wave, 48, 8, 72 + wave);
    ctx.lineTo(24, 68);
    ctx.lineTo(35, 42);
  }
  ctx.closePath();
  ctx.fill();

  // Gold trim on cape edge
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/**
 * 6-Frame Running Leg Kinematics with Bell-Bottom Flares & Gold Embroidery
 */
function drawRunLegs(ctx, frame, isIdle) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;

  let leftX = 26, leftY = 86;
  let rightX = 48, rightY = 86;

  if (isIdle) {
    leftX = 32; rightX = 46;
  } else {
    switch (frame) {
      case 1: leftX = 20; rightX = 54; break;
      case 2: leftX = 28; rightX = 46; break;
      case 3: leftX = 36; rightX = 38; break;
      case 4: leftX = 54; rightX = 20; break;
      case 5: leftX = 46; rightX = 28; break;
      case 6: leftX = 38; rightX = 36; break;
    }
  }

  // Left Leg
  ctx.beginPath();
  ctx.moveTo(34, 55);
  ctx.lineTo(leftX - 4, leftY - 4);
  ctx.lineTo(leftX + 8, leftY - 4);
  ctx.lineTo(40, 55);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Right Leg
  ctx.beginPath();
  ctx.moveTo(42, 55);
  ctx.lineTo(rightX - 4, rightY - 4);
  ctx.lineTo(rightX + 8, rightY - 4);
  ctx.lineTo(48, 55);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Golden boots
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(leftX - 6, leftY - 4, 16, 8);
  ctx.fillRect(rightX - 6, rightY - 4, 16, 8);

  // Boots lightning trim
  ctx.fillStyle = '#fde047';
  ctx.fillRect(leftX + 2, leftY - 4, 4, 4);
  ctx.fillRect(rightX + 2, rightY - 4, 4, 4);

  // Bell-bottom gold lightning piping
  drawTrousersLightning(ctx, leftX + 2, leftY - 14, rightX + 2, rightY - 14);
}

function drawTrousersLightning(ctx, x1, y1, x2, y2) {
  ctx.strokeStyle = '#fbbf24';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(x1, y1 - 10);
  ctx.lineTo(x1 - 2, y1 - 4);
  ctx.lineTo(x1 + 2, y1);
  ctx.moveTo(x2, y2 - 10);
  ctx.lineTo(x2 + 2, y2 - 4);
  ctx.lineTo(x2 - 2, y2);
  ctx.stroke();
}

/**
 * Detailed Torso with White Suit, Open V-Neck & Gold Chest Lightning
 */
function drawTorso(ctx, x, y, w, h) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;
  roundRect(ctx, x, y, w, h, 4);
  ctx.fill();
  ctx.stroke();

  // Open chest V-neck (skin)
  ctx.fillStyle = '#fed7aa';
  ctx.beginPath();
  ctx.moveTo(x + 6, y);
  ctx.lineTo(x + w - 6, y);
  ctx.lineTo(x + w / 2, y + 10);
  ctx.closePath();
  ctx.fill();

  // Gold chain / Zeus medallion
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(x + w / 2, y + 6, 3, 0, Math.PI);
  ctx.stroke();

  // Golden Zeus Lightning Bolt on chest
  ctx.fillStyle = '#eab308';
  ctx.beginPath();
  ctx.moveTo(x + w / 2 + 2, y + 8);
  ctx.lineTo(x + w / 2 - 4, y + 15);
  ctx.lineTo(x + w / 2 + 1, y + 15);
  ctx.lineTo(x + w / 2 - 2, y + 21);
  ctx.lineTo(x + w / 2 + 5, y + 13);
  ctx.lineTo(x + w / 2, y + 13);
  ctx.closePath();
  ctx.fill();
}

/**
 * Gold Eagle Belt
 */
function drawBelt(ctx, x, y, w) {
  ctx.fillStyle = '#f59e0b';
  ctx.fillRect(x - 1, y, w + 2, 5);
  // Gold Eagle Buckle
  ctx.fillStyle = '#fde047';
  ctx.fillRect(x + w / 2 - 4, y - 1, 8, 7);
  ctx.fillStyle = '#b45309';
  ctx.fillRect(x + w / 2 - 2, y + 1, 4, 3);
}

/**
 * Running Arms with Microphone Swings
 */
function drawRunArms(ctx, frame, isIdle, bob) {
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 1.5;

  let micX = 58, micY = 38 + bob;
  let leftArmX = 20, leftArmY = 40 + bob;

  if (frame === 1 || frame === 2) {
    micX = 64; micY = 32 + bob;
    leftArmX = 18; leftArmY = 44 + bob;
  } else if (frame === 4 || frame === 5) {
    micX = 52; micY = 42 + bob;
    leftArmX = 26; leftArmY = 34 + bob;
  }

  // Left arm
  ctx.beginPath();
  ctx.moveTo(32, 36 + bob);
  ctx.lineTo(leftArmX, leftArmY);
  ctx.lineTo(leftArmX + 4, leftArmY + 4);
  ctx.lineTo(34, 42 + bob);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Right arm holding golden mic
  ctx.beginPath();
  ctx.moveTo(50, 36 + bob);
  ctx.lineTo(micX - 4, micY);
  ctx.lineTo(micX, micY + 4);
  ctx.lineTo(52, 42 + bob);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Golden microphone
  drawMicrophone(ctx, micX + 2, micY - 4, 0.1);
}

/**
 * Detailed Golden Microphone with Sparkling Head
 */
function drawMicrophone(ctx, x, y, angle = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  // Handle
  ctx.fillStyle = '#d97706';
  ctx.fillRect(-2, 0, 4, 12);
  ctx.strokeStyle = '#78350f';
  ctx.lineWidth = 1;
  ctx.strokeRect(-2, 0, 4, 12);

  // Spherical Golden Grill Head
  const grad = ctx.createRadialGradient(-1, -4, 1, 0, -4, 6);
  grad.addColorStop(0, '#fef08a');
  grad.addColorStop(0.5, '#facc15');
  grad.addColorStop(1, '#ca8a04');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(0, -4, 6, 0, Math.PI * 2);
  ctx.fill();

  // Mesh lines
  ctx.strokeStyle = '#a16207';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-5, -4);
  ctx.lineTo(5, -4);
  ctx.moveTo(0, -9);
  ctx.lineTo(0, 1);
  ctx.stroke();

  // Gleam spark
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(-2, -7, 2, 2);

  ctx.restore();
}

/**
 * Detailed Head: Zeus White Beard, Golden Aviator Sunglasses, White Pompadour Hair
 */
function drawHead(ctx, x, y, lookingUp = false) {
  ctx.save();
  ctx.translate(x, y);

  // 1. Skin Face
  ctx.fillStyle = '#fed7aa';
  ctx.beginPath();
  ctx.arc(0, 0, 9, 0, Math.PI * 2);
  ctx.fill();

  // 2. Majestic Zeus White Beard & Mustache
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(-8, 0);
  ctx.quadraticCurveTo(-10, 10, -4, 15);
  ctx.quadraticCurveTo(0, 18, 4, 15);
  ctx.quadraticCurveTo(10, 10, 8, 0);
  ctx.lineTo(6, 4);
  ctx.quadraticCurveTo(0, 8, -6, 4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Curled beard locks detail
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-3, 6);
  ctx.lineTo(-2, 13);
  ctx.moveTo(2, 6);
  ctx.lineTo(2, 13);
  ctx.stroke();

  // 3. Golden Mirrored Aviator Sunglasses
  const glassY = lookingUp ? -4 : -2;
  // Left lens
  ctx.fillStyle = '#1e1b4b';
  roundRect(ctx, -7, glassY, 6, 5, 1.5);
  ctx.fill();
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  roundRect(ctx, -7, glassY, 6, 5, 1.5);
  ctx.stroke();

  // Right lens
  ctx.fillStyle = '#1e1b4b';
  roundRect(ctx, 1, glassY, 6, 5, 1.5);
  ctx.fill();
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  roundRect(ctx, 1, glassY, 6, 5, 1.5);
  ctx.stroke();

  // Bridge
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-1, glassY + 1);
  ctx.lineTo(1, glassY + 1);
  ctx.stroke();

  // White lens reflection glare
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-5, glassY + 1);
  ctx.lineTo(-3, glassY + 4);
  ctx.moveTo(3, glassY + 1);
  ctx.lineTo(5, glassY + 4);
  ctx.stroke();

  // 4. Signature Elvis White Pompadour Hair
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(-8, -4);
  ctx.quadraticCurveTo(-10, -15, 0, -16);
  ctx.quadraticCurveTo(10, -16, 9, -5);
  ctx.quadraticCurveTo(4, -9, -4, -7);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1.2;
  ctx.stroke();

  // Hair wave highlight
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-4, -13);
  ctx.quadraticCurveTo(2, -14, 6, -10);
  ctx.stroke();

  ctx.restore();
}

function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * HD Procedural Greek Meander pattern that connects seamlessly across adjacent 120px platform tiles.
 */
function drawContinuousMeander(ctx, width, topY, height, color, lw = 2) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.lineCap = 'square';
  ctx.lineJoin = 'miter';

  // 120 cleanly divides by 24 (5 units per tile)
  const unit = 24;
  const count = Math.ceil(width / unit);
  const botY = topY + height;
  const mid1 = topY + height * 0.35;
  const mid2 = topY + height * 0.65;

  ctx.beginPath();
  for (let i = 0; i < count; i++) {
    const x = i * unit;
    ctx.moveTo(x, botY);
    ctx.lineTo(x, topY);
    ctx.lineTo(x + 16, topY);
    ctx.lineTo(x + 16, mid2);
    ctx.lineTo(x + 8, mid2);
    ctx.lineTo(x + 8, mid1);
    ctx.lineTo(x + 12, mid1);
    // Connecting runner line to next repeat
    ctx.moveTo(x + 16, botY);
    ctx.lineTo(x + 24, botY);
  }
  ctx.stroke();
  ctx.restore();
}

/**
 * HD 3D Faceted Crystal Spike with metallic emitter mount, sharp specular spine, and tip glint.
 */
function draw3DFacetedSpike(ctx, w, h, brightGrad, darkGrad, ridgeColor, baseGrad, warningAccent) {
  ctx.save();
  const midX = w / 2;
  const tipY = 2;
  const baseY = h - 6;

  // 1. Heavy base mounting plate
  const bGrad = ctx.createLinearGradient(0, baseY, 0, h);
  bGrad.addColorStop(0, baseGrad[0] || '#334155');
  bGrad.addColorStop(1, baseGrad[1] || '#0f172a');
  ctx.fillStyle = bGrad;
  ctx.fillRect(1, baseY, w - 2, 6);
  ctx.strokeStyle = warningAccent || '#f59e0b';
  ctx.lineWidth = 1.5;
  ctx.strokeRect(1, baseY, w - 2, 6);

  // Emitter core diode
  ctx.fillStyle = warningAccent || '#f59e0b';
  ctx.fillRect(midX - 3, baseY + 1, 6, 4);

  // 2. Left Facet (Light-catching)
  const lGrad = ctx.createLinearGradient(0, tipY, midX, baseY);
  lGrad.addColorStop(0, brightGrad[0]);
  lGrad.addColorStop(0.5, brightGrad[1]);
  lGrad.addColorStop(1, brightGrad[2]);
  ctx.fillStyle = lGrad;
  ctx.beginPath();
  ctx.moveTo(midX, tipY);
  ctx.lineTo(2, baseY);
  ctx.lineTo(midX, baseY);
  ctx.closePath();
  ctx.fill();

  // 3. Right Facet (Shadow / Absorption)
  const rGrad = ctx.createLinearGradient(midX, tipY, w, baseY);
  rGrad.addColorStop(0, darkGrad[0]);
  rGrad.addColorStop(0.5, darkGrad[1]);
  rGrad.addColorStop(1, darkGrad[2]);
  ctx.fillStyle = rGrad;
  ctx.beginPath();
  ctx.moveTo(midX, tipY);
  ctx.lineTo(w - 2, baseY);
  ctx.lineTo(midX, baseY);
  ctx.closePath();
  ctx.fill();

  // 4. Sharp Specular Ridge (Highlight line down center)
  ctx.strokeStyle = ridgeColor || '#ffffff';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(midX, tipY);
  ctx.lineTo(midX, baseY);
  ctx.stroke();

  // 5. Outer sharp perimeter stroke
  ctx.strokeStyle = brightGrad[0];
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(2, baseY);
  ctx.lineTo(midX, tipY);
  ctx.lineTo(w - 2, baseY);
  ctx.stroke();

  // 6. Glint Star on the tip
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(midX, tipY + 1.5, 2, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

/**
 * HD Golden Master Record Collectible
 */
function drawGoldenVinyl(ctx, size) {
  ctx.save();
  const c = size / 2;
  const r = c - 2;

  // 1. Vinyl outer disc (lustrous jet black with subtle purple-gold specular sheen)
  const discGrad = ctx.createRadialGradient(c - 2, c - 2, 2, c, c, r);
  discGrad.addColorStop(0, '#1c1917');
  discGrad.addColorStop(0.7, '#0c0a09');
  discGrad.addColorStop(1, '#000000');
  ctx.fillStyle = discGrad;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fill();

  // 2. Gold outer rim
  ctx.strokeStyle = '#facc15';
  ctx.lineWidth = 1.8;
  ctx.stroke();

  // 3. Vinyl micro-groove sheen rings
  const grooveColors = ['rgba(250, 204, 21, 0.45)', 'rgba(217, 119, 6, 0.35)', 'rgba(250, 204, 21, 0.3)'];
  const grooveRadii = [r * 0.88, r * 0.76, r * 0.64, r * 0.52];
  ctx.lineWidth = 0.8;
  grooveRadii.forEach((gr, idx) => {
    ctx.strokeStyle = grooveColors[idx % grooveColors.length];
    ctx.beginPath();
    ctx.arc(c, c, gr, 0, Math.PI * 2);
    ctx.stroke();
  });

  // 4. Specular Reflection Sheen (dual fan/wedge reflections characteristic of vinyl)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.14)';
  ctx.beginPath();
  ctx.moveTo(c, c);
  ctx.arc(c, c, r - 1, -0.4, 0.4);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(c, c);
  ctx.arc(c, c, r - 1, Math.PI - 0.4, Math.PI + 0.4);
  ctx.closePath();
  ctx.fill();

  // 5. Radiant Golden Center Label
  const labelR = r * 0.38;
  const labelGrad = ctx.createRadialGradient(c - 1, c - 1, 1, c, c, labelR);
  labelGrad.addColorStop(0, '#fef08a');
  labelGrad.addColorStop(0.5, '#eab308');
  labelGrad.addColorStop(1, '#ca8a04');
  ctx.fillStyle = labelGrad;
  ctx.beginPath();
  ctx.arc(c, c, labelR, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#78350f';
  ctx.lineWidth = 1;
  ctx.stroke();

  // 6. Music Note icon on label (♪)
  ctx.fillStyle = '#1c1917';
  ctx.font = 'bold 9px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('♪', c, c);

  // 7. Center Spindle Hole (metallic brass ring + black void)
  ctx.strokeStyle = '#fde047';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(c, c, 3.2, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = '#09090b';
  ctx.beginPath();
  ctx.arc(c, c, 2.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

/**
 * HD Plasma Orb Hazard
 */
function drawPlasmaOrb(ctx, size, cCore, cMantle, cOuter, cSparks) {
  ctx.save();
  const c = size / 2;
  const r = c - 2;

  // 1. Coronal glow
  const corona = ctx.createRadialGradient(c, c, r * 0.4, c, c, r);
  corona.addColorStop(0, cOuter);
  corona.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = corona;
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.fill();

  // 2. Plasma Core Mantle
  const mantle = ctx.createRadialGradient(c - 2, c - 2, 2, c, c, r * 0.8);
  mantle.addColorStop(0, '#ffffff');
  mantle.addColorStop(0.3, cCore);
  mantle.addColorStop(0.7, cMantle);
  mantle.addColorStop(1, cOuter);
  ctx.fillStyle = mantle;
  ctx.beginPath();
  ctx.arc(c, c, r * 0.8, 0, Math.PI * 2);
  ctx.fill();

  // 3. Energetic electric rim
  ctx.strokeStyle = cSparks;
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(c, c, r * 0.8, 0, Math.PI * 2);
  ctx.stroke();

  // 4. Electric plasma arcs around perimeter
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 3; i++) {
    const a = (i * Math.PI * 2) / 3;
    ctx.beginPath();
    ctx.moveTo(c + Math.cos(a) * (r * 0.35), c + Math.sin(a) * (r * 0.35));
    ctx.quadraticCurveTo(
      c + Math.cos(a + 0.6) * (r * 0.95),
      c + Math.sin(a + 0.6) * (r * 0.95),
      c + Math.cos(a + 1.1) * (r * 0.6),
      c + Math.sin(a + 1.1) * (r * 0.6)
    );
    ctx.stroke();
  }

  // 5. White-hot center glint
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(c - 2, c - 2, 3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}

/**
 * HD Titanium Rotary Saw Blade
 */
function drawSawBlade(ctx, size, cTungsten, cEdge, cGlow, cHub) {
  ctx.save();
  const cx = size / 2, cy = size / 2, outerR = cx - 2, innerR = outerR * 0.65;
  const numTeeth = 12;

  // Blade teeth
  ctx.beginPath();
  for (let i = 0; i < numTeeth; i++) {
    const a1 = (i / numTeeth) * Math.PI * 2;
    const a2 = ((i + 0.4) / numTeeth) * Math.PI * 2;
    const a3 = ((i + 0.95) / numTeeth) * Math.PI * 2;
    ctx.lineTo(cx + Math.cos(a1) * outerR, cy + Math.sin(a1) * outerR);
    ctx.quadraticCurveTo(
      cx + Math.cos(a2) * (outerR * 0.85),
      cy + Math.sin(a2) * (outerR * 0.85),
      cx + Math.cos(a3) * innerR,
      cy + Math.sin(a3) * innerR
    );
  }
  ctx.closePath();

  // Radial sheen fill
  const steelGrad = ctx.createRadialGradient(cx - 3, cy - 3, 2, cx, cy, outerR);
  steelGrad.addColorStop(0, '#e2e8f0');
  steelGrad.addColorStop(0.4, cTungsten);
  steelGrad.addColorStop(0.85, '#1e293b');
  steelGrad.addColorStop(1, cEdge);
  ctx.fillStyle = steelGrad;
  ctx.fill();

  ctx.strokeStyle = cGlow;
  ctx.lineWidth = 1.8;
  ctx.stroke();

  // Expansion relief slots
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 4; i++) {
    const a = (i * Math.PI) / 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * (innerR * 0.4), cy + Math.sin(a) * (innerR * 0.4));
    ctx.lineTo(cx + Math.cos(a) * (innerR * 0.85), cy + Math.sin(a) * (innerR * 0.85));
    ctx.stroke();
  }

  // Heavy titanium center arbor hub
  ctx.fillStyle = cHub || '#f59e0b';
  ctx.beginPath();
  ctx.arc(cx, cy, 6, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#0f172a';
  ctx.beginPath();
  ctx.arc(cx, cy, 3, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}
